import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, FileText, Plus } from "lucide-react";
import { useDemo } from "@/store/DemoStore";
import { Card, Empty, PageHeader, Segmented, StatusBadge, btn } from "@/components/common";
import { NewQuoteDialog } from "@/components/NewQuoteDialog";
import { ConvertButton } from "@/pages/agent/TripWorkspace";
import { fmtDate, fmtMoney, tripTotal } from "@/lib/trip";

function QuoteCard({ t, client }) {
  return (
    <Card className="flex flex-col overflow-hidden sm:flex-row" data-testid={`quote-card-${t.id}`}>
      <img src={t.image} alt="" loading="lazy" className="h-32 w-full shrink-0 object-cover sm:h-auto sm:w-44" />
      <div className="flex min-w-0 flex-1 flex-col gap-4 p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-mono text-xs text-slate-400">{t.quoteNumber}</p>
            <p className="mt-0.5 truncate font-semibold text-navy-900">{t.title}</p>
            <p className="truncate text-sm text-slate-500">{client.name} · {t.vessel}</p>
          </div>
          <StatusBadge status={t.status} className="shrink-0" />
        </div>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="text-xs text-slate-500">
            <p>Departs {fmtDate(t.departure)} · {t.nights} nights</p>
            <p className="mt-0.5">{t.status === "QUOTE" ? `Prepared ${fmtDate(t.createdOn || t.bookedOn)}` : `Booking ${t.bookingNumber}`}</p>
          </div>
          <p className="font-display text-lg font-bold text-navy-900">{fmtMoney(tripTotal(t), true)}</p>
        </div>
        <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-4">
          <Link to={`/agent/quotes/${t.id}`} data-testid={`quote-view-${t.id}`} className={btn.secondary}>View Quote</Link>
          {t.status === "QUOTE" ? <ConvertButton trip={t} /> : <Link to={`/agent/trips/${t.id}`} className={btn.ghost} data-testid={`quote-open-trip-${t.id}`}>Open trip <ArrowRight className="h-4 w-4" /></Link>}
        </div>
      </div>
    </Card>
  );
}

export default function QuotesPage() {
  const { trips, client } = useDemo();
  const [params, setParams] = useSearchParams();
  const [filter, setFilter] = useState("OPEN");
  const open = params.get("new") === "1";
  const all = trips.filter((t) => t.status === "QUOTE" || t.createdOn);
  const list = all.filter((t) => filter === "ALL" || (filter === "OPEN" ? t.status === "QUOTE" : t.status !== "QUOTE"));
  const setOpen = (v) => setParams(v ? { new: "1" } : {}, { replace: true });

  return (
    <div>
      <PageHeader title="Quotes" subtitle="Build proposals and convert them into booked trips." actions={<button data-testid="create-quote-btn" onClick={() => setOpen(true)} className={btn.primary}><Plus className="h-4 w-4" />New Quote</button>} />
      <div className="mb-5">
        <Segmented testPrefix="quotes-filter" value={filter} onChange={setFilter} options={[
          { value: "OPEN", label: "Open", count: all.filter((t) => t.status === "QUOTE").length },
          { value: "CONVERTED", label: "Converted", count: all.filter((t) => t.status !== "QUOTE").length },
          { value: "ALL", label: "All", count: all.length },
        ]} />
      </div>
      <div className="grid gap-4 xl:grid-cols-2" data-testid="quotes-list">
        {list.map((t) => <QuoteCard key={t.id} t={t} client={client(t.clientId)} />)}
      </div>
      {!list.length && <Empty icon={FileText} title="Nothing here yet" text="Create a quote to get started." />}
      {open && <NewQuoteDialog open onOpenChange={setOpen} defaultClient={params.get("client")} />}
    </div>
  );
}
